import { ReactNode, useEffect, useMemo, useState } from 'react';

import { Result } from '../types';
import ResultContext from './ResultContext';

type PropType = {
  children: ReactNode[] | ReactNode;
};

const ResultContextProvider = (props: PropType) => {
  const [lockedAge, setLockedAge] = useState<number>(0);
  const [result, setResult] = useState<Partial<Result>>({
    echteLeeftijd: 0,
    inputLeeftijd: 0,
    surveyId: 0,
    topImprovements: [],
    topCompliments: [],
  });

  const [isModified, setIsModified] = useState<boolean>(
    () => localStorage.getItem('gezondleven-is-modified') === 'true'
  );

  const [lockedCategories, setLockedCategories] = useState(
    () =>
      JSON.parse(localStorage.getItem('gezondleven-locked-categories') ?? 'null') || {
        improvements: [],
        compliments: [],
      }
  );

  const updateIsModified = (value: boolean) => {
    localStorage.setItem('gezondleven-is-modified', String(value));
    setIsModified(value);
  };

  const updateLockedCategories = (newLockedCategories: { improvements: string[]; compliments: string[] }) => {
    localStorage.setItem('gezondleven-locked-categories', JSON.stringify(newLockedCategories));
    setLockedCategories(newLockedCategories);
  };

  const lockCategories = (type: 'improvements' | 'compliments', categories: string[]) => {
    const updated = {
      ...lockedCategories,
      [type]: categories,
    };
    localStorage.setItem('gezondleven-locked-categories', JSON.stringify(updated));
    setLockedCategories(updated);
  };

  const topListMapper = (item: string) => {
    switch (item) {
      case 'Alcohol':
        return 'Alcohol';
      case 'Cholesterol':
      case 'Triglicerides':
      case 'Hdl':
        return 'Cholesterol';
      case 'Diet':
        return 'Nutrition';
      case 'HyperTension':
        return 'Vitals';
      case 'Bmi':
        return 'Weight';
      case 'Smoking':
        return 'Smoking';
      case 'Coffee':
        return 'Coffee';
      case 'Exersize':
        return 'Exersize';
      case 'Sleep':
        return 'Sleep';
      case 'Stress':
        return 'Stress';
      case 'CopdMother':
      case 'CopdFather':
      case 'T2DMother':
      case 'CvdMother':
      case 'Age':
      case 'Sex':
      case 'PaidWork':
      case 'Education':
      default:
        return 'Personal';
    }
  };

  const processTopList = (items: string[]): string[] => items.map((item) => topListMapper(item));

  const updateResult = (newResult: Result) => {
    const processedResult = {
      ...newResult,
      topCompliments: processTopList(newResult.topCompliments),
      topImprovements: processTopList(newResult.topImprovements),
    };
    localStorage.setItem('gezondleven-result', JSON.stringify(newResult));
    setResult(processedResult);
  };

  const updateInitialResult = (newResult: Result) => {
    localStorage.removeItem('gezondleven-is-modified');
    localStorage.removeItem('gezondleven-locked-categories');
    localStorage.setItem('gezondleven-result-backup', JSON.stringify(newResult));
    updateLockedCategories({
      improvements: processTopList(newResult.topImprovements),
      compliments: processTopList(newResult.topCompliments),
    });
    setLockedAge(newResult.echteLeeftijd);
    setIsModified(false);
    updateResult(newResult);
  };

  const resetResults = () => {
    const initialResults = JSON.parse(localStorage.getItem('gezondleven-result-backup') ?? 'null');
    if (initialResults) {
      updateInitialResult(initialResults);
    }
  };

  useEffect(() => {
    const storedResult = localStorage.getItem('gezondleven-result');
    if (storedResult) {
      const parsedResult = JSON.parse(storedResult);
      const processedResult = {
        ...parsedResult,
        topCompliments: processTopList(parsedResult.topCompliments || []),
        topImprovements: processTopList(parsedResult.topImprovements || []),
      };
      setResult(processedResult);
    }
  }, []);

  const { children } = props;
  const context = useMemo(
    () => ({
      result,
      updateResult,
      updateInitialResult,
      isModified,
      updateIsModified,
      lockedAge,
      lockedCategories,
      lockCategories,
      resetResults,
    }),
    [result, isModified, lockedCategories, lockedAge]
  );

  return <ResultContext.Provider value={context}>{children}</ResultContext.Provider>;
};

export default ResultContextProvider;
